import { NextRouter, useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Input } from '../../../design-system/components/form/input/Input';
import { ItinerariesListAvailableFilters } from '../../../resources/itinerary/domain/types/Itinerary';
import { useLocale } from '../../../shared/hooks/useLocale';
import { useCurrency } from '../../../shared/providers/currency/useCurrency';
import { getCurrencySymbol } from '../../../shared/utilities/priceFormat/priceFormat';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import style from './PriceFilter.module.css';

type PriceFilterProps = {
    priceFilter: PickDomain<ItinerariesListAvailableFilters, 'filters'>['filters'][number];
    handleFilters: (
        filters: {
            priceMin?: string;
            priceMax?: string;
        },
        router: NextRouter,
    ) => void;
};

export const PriceFilter = ({ priceFilter, handleFilters }: PriceFilterProps) => {
    const { currentCurrency } = useCurrency();
    const { locale } = useLocale();
    const [focusedInput, setFocusedInput] = useState<string | null>(null);
    const [priceMin, setPriceMin] = useState<string | undefined>();
    const [priceMax, setPriceMax] = useState<string | undefined>();
    const { formatMessage } = useIntl();
    const router = useRouter();
    const { trackEvent } = useTrackEventAmplitude();

    useEffect(() => {
        const { priceMin, priceMax } = router.query;
        setPriceMin(priceMin ? String(priceMin) : '');
        setPriceMax(priceMax ? String(priceMax) : '');
    }, [priceFilter?.min, priceFilter?.max, router.query]);

    const handlePriceChange = useCallback(
        (min: string | undefined, max: string | undefined) => {
            const updatedQueryParams = {
                priceMin: min !== undefined && min !== '' ? min : '0',
                priceMax:
                    max !== undefined && max !== '' && max !== '0' ? max : String(priceFilter.max),
            };

            handleFilters(updatedQueryParams, router);
        },
        [handleFilters, priceFilter.max, router],
    );

    if (!priceFilter) return null;

    return (
        <div>
            <div className={style.priceContainer}>
                <Input
                    placeholder={String(priceFilter.min)}
                    inputProps={{
                        name: 'priceMin',
                        min: priceFilter.min,
                        onChange: (e) => {
                            setFocusedInput('min');
                            const { value } = e.target;
                            setPriceMin(value);
                            handlePriceChange(value, priceMax);
                        },
                        onBlur: (e) => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.listItinerariesFilterSelected({
                                    ...defaultProperties,
                                    filter_name: 'price-min',
                                    filter_value: e.target.value,
                                });
                            });
                        },
                        type: 'number',
                        value: priceMin,
                        autoFocus: focusedInput === 'min',
                    }}
                    unit={getCurrencySymbol({ locale, currency: currentCurrency })}
                    label={formatMessage({
                        id: `page.itinerariesList.filters.price.min`,
                    })}
                />
                <Input
                    placeholder={String(priceFilter.max)}
                    inputProps={{
                        name: 'priceMax',
                        min: priceFilter.max,
                        onChange: (e) => {
                            setFocusedInput('max');
                            const { value } = e.target;
                            setPriceMax(value);
                            handlePriceChange(priceMin, value);
                        },
                        onBlur: (e) => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.listItinerariesFilterSelected({
                                    ...defaultProperties,
                                    filter_name: 'price-max',
                                    filter_value: e.target.value,
                                });
                            });
                        },
                        value: priceMax,
                        type: 'number',
                        autoFocus: focusedInput === 'max',
                    }}
                    unit={getCurrencySymbol({ locale, currency: currentCurrency })}
                    label={formatMessage({
                        id: `page.itinerariesList.filters.price.max`,
                    })}
                />
            </div>
        </div>
    );
};
